import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const faqs = [
  {
    question: 'How do I create an account?',
    answer:
      'Download Recall or visit the web app, tap Sign Up and enter your email address, create a password and verify your email. You\'re ready to start studying!',
  },
  {
    question: 'How do I create a deck?',
    answer:
      'From the home screen, tap Create Deck. Give your deck a name and optional description, then tap Add Card to add flashcards. Enter the front (question) and back (answer) for each card. Your cards are saved automatically and synced across devices.',
  },
  {
    question: 'How do I study?',
    answer:
      'Select a deck from your home screen and tap Study to begin a review session. When a card appears, try to recall the answer before revealing it. Rate your recall (Again, Hard, Good, Easy) to help the algorithm schedule your next review. Cards you find difficult will appear more frequently; cards you know well will appear less often.',
  },
  {
    question: 'How does spaced repetition work?',
    answer:
      'Spaced repetition is a learning technique that shows you flashcards at increasing intervals based on how well you know them. Cards you struggle with appear more often, while cards you know well are shown less frequently. This approach is backed by decades of cognitive science research and is one of the most effective ways to commit information to long-term memory.',
  },
  {
    question: 'What is the FSRS algorithm?',
    answer:
      'FSRS (Free Spaced Repetition Scheduler) is a modern, open-source spaced repetition algorithm. Recall uses FSRS-4.5, which provides more accurate scheduling than traditional algorithms by modelling how memories strengthen and decay over time. It adapts to your individual learning patterns to optimise your study sessions.',
  },
  {
    question: 'Can Recall build flashcards from my notes?',
    answer:
      'Yes — paste your class notes into Generate with AI and Recall turns them into a ready-to-study deck, one testable fact per card, with maths notation and code formatting preserved. You can review, edit and delete cards before saving. Free accounts include 3 generations; Premium unlocks unlimited.',
  },
  {
    question: 'Is there ready-made GCSE content?',
    answer:
      'Yes — Recall ships with a built-in GCSE library covering 22 subjects, from Maths and the sciences to English Literature, History, Geography and languages. Add a subject and start studying in seconds. Library cards don\'t count toward the free plan\'s card limit.',
  },
  {
    question: 'Can I study offline?',
    answer:
      'Yes! Recall is designed to work offline. Your flashcards are stored locally on your device, so you can study without an internet connection. Any changes you make while offline will be synced automatically when you reconnect.',
  },
  {
    question: 'What\'s included in the free tier?',
    answer:
      'The free tier includes up to 200 of your own cards (the built-in GCSE library doesn\'t count toward the cap), full spaced repetition scheduling with FSRS-4.5, 3 AI deck generations to try, offline study, cross-device sync, and 1 streak freeze per week to protect your study streak if you miss a day.',
  },
  {
    question: 'What do I get with Premium?',
    answer:
      'Premium unlocks unlimited cards (no cap on how many flashcards you can create), unlimited AI deck generation, unlimited streak freezes (never lose your study streak), and advanced analytics with detailed insights into your learning patterns, retention rates, and study habits.',
  },
  {
    question: 'What are the subscription plans?',
    answer:
      'Recall Premium is £19.99/year — including a 1-month free trial — or £6.99/month. All subscriptions are purchased and managed through Apple In-App Purchase in the iOS app.',
  },
  {
    question: 'How do I cancel my subscription?',
    answer:
      'Go to your device\'s Settings > Apple ID > Subscriptions > Recall > Cancel Subscription. Apple manages all Recall subscriptions. Your premium features will remain active until the end of your current billing period.',
  },
  {
    question: 'How do I delete my account?',
    answer:
      'Go to Settings in the app and select Delete Account. This will permanently remove your account and all associated data including flashcard content, study history, and statistics. This action is irreversible. Alternatively, contact us at support@wolftechnologies.co.uk.',
  },
];

const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className={`faq__item ${isOpen ? 'faq__item--open' : ''}`} onClick={onClick}>
    <div className="faq__question">
      <h3>{question}</h3>
      <svg
        className="faq__chevron"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M6 9l6 6 6-6" />
      </svg>
    </div>
    {isOpen && (
      <div className="faq__answer">
        <p>{answer}</p>
      </div>
    )}
  </div>
);

const RecallSupport = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="legal-page">
      <div className="legal-page__header">
        <div className="container">
          <Link to="/" className="legal-page__back">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" />
              <path d="M12 19l-7-7 7-7" />
            </svg>
            Back to Home
          </Link>
          <span className="section__tag">Support</span>
          <h1 className="legal-page__title">Recall Support</h1>
          <p className="legal-page__subtitle">
            Get help with Recall - Smart Flashcards by Wolf Technologies Ltd
          </p>
        </div>
      </div>

      <div className="container">
        <div className="legal-page__content">

          {/* Contact Section */}
          <section className="support-contact">
            <div className="support-contact__card">
              <div className="support-contact__icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </div>
              <div>
                <h3>Need help?</h3>
                <p>
                  Contact us at{' '}
                  <a href="mailto:support@wolftechnologies.co.uk">support@wolftechnologies.co.uk</a>
                </p>
                <p className="support-contact__response">
                  We aim to respond to all enquiries within 24 hours.
                </p>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="legal-section">
            <h2>Frequently Asked Questions</h2>
            <div className="faq__list">
              {faqs.map((faq, index) => (
                <FAQItem
                  key={index}
                  question={faq.question}
                  answer={faq.answer}
                  isOpen={openIndex === index}
                  onClick={() => toggleFAQ(index)}
                />
              ))}
            </div>
          </section>

          {/* App Info */}
          <section className="legal-section">
            <h2>App Information</h2>
            <div className="support-info__grid">
              <div className="support-info__item">
                <span className="support-info__label">App</span>
                <span className="support-info__value">Recall - Smart Flashcards</span>
              </div>
              <div className="support-info__item">
                <span className="support-info__label">Version</span>
                <span className="support-info__value">1.0</span>
              </div>
              <div className="support-info__item">
                <span className="support-info__label">Developer</span>
                <span className="support-info__value">Wolf Technologies Ltd</span>
              </div>
              <div className="support-info__item">
                <span className="support-info__label">Platform</span>
                <span className="support-info__value">iOS</span>
              </div>
            </div>
          </section>

          {/* Links */}
          <section className="legal-section">
            <h2>Useful Links</h2>
            <div className="support-links">
              <Link to="/recall/privacy" className="support-links__item">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0110 0v4" />
                </svg>
                Privacy Policy
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14" />
                  <path d="M12 5l7 7-7 7" />
                </svg>
              </Link>
              <a href="/" className="support-links__item">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
                WolfTechnologies Home
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14" />
                  <path d="M12 5l7 7-7 7" />
                </svg>
              </a>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default RecallSupport;
